import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { VerifyBadge } from "../components/Badges";
import { Funnel, MagnifyingGlass, MapPin, Briefcase, ArrowRight, Star } from "@phosphor-icons/react";

export default function Search() {
  const { t } = useI18n();
  const [skills, setSkills] = useState([]);
  const [skill, setSkill] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState("");
  const [minExp, setMinExp] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [results, setResults] = useState({ results: [], count: 0 });

  useEffect(() => { api.get("/skills").then(({ data }) => setSkills(data.skills)); }, []);

  const run = async () => {
    const params = new URLSearchParams();
    if (skill) params.set("skill", skill);
    if (city) params.set("city", city);
    if (role) params.set("role", role);
    if (minExp) params.set("min_experience", minExp);
    if (verifiedOnly) params.set("verified_only", "true");
    const { data } = await api.get(`/search/talent?${params.toString()}`);
    setResults(data);
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [skill, city, role, minExp, verifiedOnly]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">{t("nav_search")}</div>
        <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("search_talent")}</h1>

        <div className="as-card p-5 grid md:grid-cols-5 gap-3 mt-6">
          <div className="md:col-span-2">
            <label className="as-label flex items-center gap-1.5"><Funnel size={14} weight="duotone" />{t("filter_skill")}</label>
            <select className="as-input" value={skill} onChange={(e) => setSkill(e.target.value)} data-testid="search-skill">
              <option value="">All skills</option>
              {skills.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="as-label">{t("filter_city")}</label>
            <input className="as-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Hyderabad" data-testid="search-city" />
          </div>
          <div>
            <label className="as-label">Role</label>
            <select className="as-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="search-role">
              <option value="">All</option>
              <option value="skilled_individual">Individual</option>
              <option value="msme">MSME</option>
            </select>
          </div>
          <div>
            <label className="as-label">{t("filter_min_exp")}</label>
            <input className="as-input" type="number" min={0} value={minExp} onChange={(e) => setMinExp(e.target.value)} data-testid="search-min-exp" />
          </div>
          <label className="md:col-span-5 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="w-5 h-5" data-testid="search-verified" />
            <span className="font-bold text-[var(--as-primary)]">{t("filter_verified")}</span>
          </label>
        </div>

        <div className="mt-6 flex items-center gap-2 text-sm text-[var(--as-text-secondary)]">
          <MagnifyingGlass size={16} weight="duotone" />{results.count} {results.count === 1 ? "result" : "results"}
        </div>

        {results.results.length === 0 ? (
          <div className="as-card p-10 mt-4 text-center text-[var(--as-text-secondary)]">{t("no_results")}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {results.results.map((r) => (
              <Link to={`/profile/${r.user.id}`} key={r.user.id} className="as-card p-6 hover:-translate-y-1" data-testid={`search-result-${r.user.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[var(--as-bg-alt)] grid place-items-center text-2xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
                    {r.user.name?.[0] || "?"}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{r.user.name}</div>
                      <span className="as-badge-pill">{t("match_score")}: {r.score}</span>
                    </div>
                    <div className="text-xs text-[var(--as-text-secondary)] mt-0.5">{r.user.role.replace("_"," ")} · {r.user.years_experience || 0} yrs</div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-[var(--as-text-secondary)]">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} weight="duotone" />{r.user.city || "—"}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase size={12} weight="duotone" />{r.media_count} media</span>
                      <VerifyBadge status={r.user.kyc_status} />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(r.user.skills || []).slice(0, 4).map((s) => <span key={s} className="as-badge-skill !text-xs">{s}</span>)}
                    </div>
                  </div>
                  <ArrowRight size={18} weight="bold" className="text-[var(--as-text-secondary)]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
